import { useEffect, useRef } from 'react';
import { apiClient } from '../api/client';

/**
 * useInvestorTelemetry — fire-and-forget per-investor tab telemetry.
 *
 * Use inside any investor-facing surface (DataRoom tabs, InvestorPortal
 * sections). On mount it POSTs an `open` event; every 15s it POSTs a
 * `heartbeat` with the cumulative dwell-time; on unmount it POSTs a `close`.
 *
 * Backend ingest:  POST /api/vault/investor-telemetry/event  (no vault auth)
 *
 * Failures are silently swallowed — this is observability, not a UX gate.
 */
const STORAGE_KEY = 'hibff_investor_telemetry_session';

const getOrCreateSessionId = () => {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const id = `vts_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    return `vts_${Date.now()}`;
  }
};

const post = (body) => {
  // Use sendBeacon when leaving the page so close events survive unload.
  try {
    if (body.action === 'close' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(`${apiClient.baseUrl}/api/vault/investor-telemetry/event`, blob);
      return;
    }
  } catch {}
  apiClient.request('/api/vault/investor-telemetry/event', {
    method: 'POST',
    body: JSON.stringify(body),
  }).catch(() => {});
};

export function useInvestorTelemetry(investorId, tab, enabled = true) {
  const startRef = useRef(null);
  const sessionRef = useRef(null);
  const heartbeatRef = useRef(null);

  useEffect(() => {
    if (!enabled || !investorId || !tab) return undefined;
    sessionRef.current = getOrCreateSessionId();
    startRef.current = Date.now();

    const sid = sessionRef.current;
    post({ investor_id: investorId, tab, action: 'open', session_id: sid, duration_seconds: 0 });

    heartbeatRef.current = setInterval(() => {
      const secs = Math.max(0, Math.floor((Date.now() - startRef.current) / 1000));
      post({ investor_id: investorId, tab, action: 'heartbeat', session_id: sid, duration_seconds: secs });
    }, 15000);

    const beforeUnload = () => {
      const secs = Math.max(0, Math.floor((Date.now() - startRef.current) / 1000));
      post({ investor_id: investorId, tab, action: 'close', session_id: sid, duration_seconds: secs });
    };
    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      window.removeEventListener('beforeunload', beforeUnload);
      const secs = Math.max(0, Math.floor((Date.now() - startRef.current) / 1000));
      post({ investor_id: investorId, tab, action: 'close', session_id: sid, duration_seconds: secs });
    };
  }, [investorId, tab, enabled]);
}

export default useInvestorTelemetry;
